import { api, ApiError, getAdminKey } from '../api';
import { statusBadge, tierBadge } from '../components/badge';
import { el, errorBanner, skeletonTable , appendAll} from '../components/layout';
import { progressBar } from '../components/progress';
import { dataTable } from '../components/table';

type Run = {
  id: number;
  client: string;
  market: string;
  status: string;
  target_count: number;
  progress: string | null;
  error: string | null;
  created_at: string;
  geo_cities?: string;
  categories?: string;
};

type Result = {
  id: number;
  business_name: string;
  city: string | null;
  category: string | null;
  website: string | null;
  phone: string | null;
  email: string | null;
  decision_maker: string | null;
  fit_score: number | null;
  tier: string | null;
  why_good_buyer: string | null;
  likely_products: string | null;
  call_notes: string | null;
  approved: number;
};

let pollTimer: number | null = null;

function stopPoll() {
  if (pollTimer != null) {
    window.clearInterval(pollTimer);
    pollTimer = null;
  }
}

export function disposeRunDetailPage() {
  stopPoll();
}

export function renderRunDetailPage(root: HTMLElement, runId: number) {
  stopPoll();
  const content = el('div');
  appendAll(content, el('div', { className: 'page-header' }, [
      el('h1', { text: `Run #${runId}` }),
      el('a', { href: '#/runs', className: 'btn', text: 'Back to runs' }),
    ]),
  );

  const metaHost = el('div');
  metaHost.appendChild(skeletonTable(3));
  const actionBar = el('div', { className: 'row-actions', style: 'margin-bottom:16px' });
  const filterBar = el('div', { className: 'toolbar' });
  const tableHost = el('div');
  appendAll(content, metaHost, actionBar, filterBar, tableHost);
  root.appendChild(content);

  let run: Run | null = null;
  let results: Result[] = [];
  let tierFilter: 'all' | 'A' | 'B' | 'C' = 'all';
  let approvedOnly = false;
  let sortKey = 'fit_score';
  let sortDir: 'asc' | 'desc' = 'desc';
  let expandedId: number | null = null;

  function filteredSorted(): Result[] {
    let rows = [...results];
    if (tierFilter !== 'all') {
      rows = rows.filter((r) => String(r.tier || '').toUpperCase() === tierFilter);
    }
    if (approvedOnly) rows = rows.filter((r) => Number(r.approved) === 1);
    rows.sort((a, b) => {
      const av = (a as Record<string, unknown>)[sortKey];
      const bv = (b as Record<string, unknown>)[sortKey];
      const an = typeof av === 'number' ? av : String(av ?? '');
      const bn = typeof bv === 'number' ? bv : String(bv ?? '');
      if (an < bn) return sortDir === 'asc' ? -1 : 1;
      if (an > bn) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }

  function paintMeta() {
    if (!run) return;
    const card = el('div', { className: 'card' });
    const grid = el('div', { className: 'meta-grid' }, [
      el('div', {}, [
        el('div', { className: 'k', text: 'Client' }),
        el('div', { className: 'v', text: run.client }),
      ]),
      el('div', {}, [
        el('div', { className: 'k', text: 'Market' }),
        el('div', { className: 'v', text: run.market }),
      ]),
      el('div', {}, [
        el('div', { className: 'k', text: 'Status' }),
        el('div', { className: 'v' }, [statusBadge(run.status)]),
      ]),
      el('div', {}, [
        el('div', { className: 'k', text: 'Target' }),
        el('div', { className: 'v', text: String(run.target_count) }),
      ]),
      el('div', {}, [
        el('div', { className: 'k', text: 'Created' }),
        el('div', {
          className: 'v',
          text: String(run.created_at || '').replace('T', ' ').slice(0, 19),
        }),
      ]),
    ]);
    card.appendChild(grid);
    if (run.status === 'running' || run.status === 'queued') {
      card.appendChild(el('div', { style: 'margin-top:14px' }, [progressBar(run.progress, run.target_count)]));
    }
    if (run.error) {
      appendAll(card, el('div', {
          className: 'banner-error',
          style: 'margin-top:14px;margin-bottom:0',
          text: run.error,
        }),
      );
    }
    metaHost.replaceChildren(card);
  }

  function paintActions() {
    actionBar.replaceChildren(
      el('button', {
        className: 'btn btn-primary',
        type: 'button',
        text: 'Approve All',
        onClick: async () => {
          try {
            await api(`/api/runs/${runId}/approve`, { method: 'POST', body: '{}' });
            await load();
          } catch (err) {
            alert(err instanceof Error ? err.message : String(err));
          }
        },
      }),
      el('button', {
        className: 'btn',
        type: 'button',
        text: 'Export CSV',
        onClick: async () => {
          try {
            const key = getAdminKey();
            const res = await fetch(`/api/runs/${runId}/export`, {
              headers: key ? { Authorization: `Bearer ${key}` } : {},
            });
            if (!res.ok) {
              const data = await res.json().catch(() => ({}));
              throw new Error((data as { error?: string }).error || `Export ${res.status}`);
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `run-${runId}-leads.csv`;
            a.click();
            URL.revokeObjectURL(url);
            await load();
          } catch (err) {
            alert(err instanceof Error ? err.message : String(err));
          }
        },
      }),
    );
  }

  function paintFilters() {
    const tierWrap = el('div', { className: 'row-actions' });
    for (const t of ['all', 'A', 'B', 'C'] as const) {
      appendAll(tierWrap, el('button', {
          className: `btn${tierFilter === t ? ' btn-primary' : ''}`,
          type: 'button',
          text: t === 'all' ? 'All tiers' : `Tier ${t}`,
          onClick: () => {
            tierFilter = t;
            paintTable();
            paintFilters();
          },
        }),
      );
    }
    const approvedBtn = el('button', {
      className: `btn${approvedOnly ? ' btn-primary' : ''}`,
      type: 'button',
      text: approvedOnly ? 'Approved only' : 'All approvals',
      onClick: () => {
        approvedOnly = !approvedOnly;
        paintTable();
        paintFilters();
      },
    });
    filterBar.replaceChildren(tierWrap, approvedBtn);
  }

  function paintTable() {
    const rows = filteredSorted();
    const wrap = el('div');
    const table = dataTable<Result>({
      sortKey,
      sortDir,
      onSort: (key) => {
        if (sortKey === key) sortDir = sortDir === 'asc' ? 'desc' : 'asc';
        else {
          sortKey = key;
          sortDir = key === 'fit_score' ? 'desc' : 'asc';
        }
        paintTable();
      },
      columns: [
        {
          key: 'approved',
          label: '',
          render: (r) => {
            const cb = el('input', {
              type: 'checkbox',
            }) as HTMLInputElement;
            cb.checked = Number(r.approved) === 1;
            cb.addEventListener('change', async () => {
              try {
                await api(`/api/runs/${runId}/results/${r.id}/approve`, {
                  method: 'POST',
                  body: JSON.stringify({ approved: cb.checked }),
                });
                r.approved = cb.checked ? 1 : 0;
              } catch (err) {
                cb.checked = !cb.checked;
                alert(err instanceof Error ? err.message : String(err));
              }
            });
            return cb;
          },
        },
        {
          key: 'tier',
          label: 'Tier',
          sortable: true,
          render: (r) => tierBadge(r.tier),
        },
        {
          key: 'fit_score',
          label: 'Score',
          sortable: true,
          render: (r) => String(r.fit_score ?? '-'),
        },
        {
          key: 'business_name',
          label: 'Business',
          sortable: true,
          render: (r) => r.business_name || '-',
        },
        { key: 'city', label: 'City', sortable: true, render: (r) => r.city || '-' },
        {
          key: 'category',
          label: 'Category',
          sortable: true,
          render: (r) => r.category || '-',
        },
        { key: 'phone', label: 'Phone', render: (r) => r.phone || '-' },
        { key: 'email', label: 'Email', render: (r) => r.email || '-' },
        {
          key: 'website',
          label: 'Website',
          render: (r) => {
            if (!r.website) return '-';
            const href = /^https?:/i.test(r.website) ? r.website : `https://${r.website}`;
            return el('a', {
              href,
              target: '_blank',
              rel: 'noopener',
              text: r.website.replace(/^https?:\/\//i, '').slice(0, 32),
            });
          },
        },
        {
          key: 'decision_maker',
          label: 'Decision Maker',
          render: (r) => r.decision_maker || '-',
        },
        {
          key: 'why_good_buyer',
          label: 'Why Good Buyer',
          render: (r) => {
            const text = (r.why_good_buyer || '').slice(0, 80);
            return el('span', {
              className: 'why-cell',
              text: text ? `${text}${(r.why_good_buyer || '').length > 80 ? '...' : ''}` : '-',
              onClick: (ev: Event) => {
                ev.stopPropagation();
                expandedId = expandedId === r.id ? null : r.id;
                paintTable();
              },
            });
          },
        },
      ],
      rows,
      rowClass: (r) => (expandedId === r.id ? 'expanded' : ''),
    });
    wrap.appendChild(table);

    if (expandedId != null) {
      const row = results.find((r) => r.id === expandedId);
      if (row) {
        appendAll(wrap, el('div', { className: 'drawer' }, [
            el('div', {
              text: `Why: ${row.why_good_buyer || '-'}`,
            }),
            el('div', {
              style: 'margin-top:8px',
              text: `Likely products: ${row.likely_products || '-'}`,
            }),
            el('div', {
              style: 'margin-top:8px',
              text: `Call notes: ${row.call_notes || '-'}`,
            }),
          ]),
        );
      }
    }

    tableHost.replaceChildren(wrap);
  }

  async function load() {
    try {
      const data = await api<{ run: Run; results: Result[] }>(`/api/runs/${runId}`);
      run = data.run;
      results = data.results || [];
      paintMeta();
      paintActions();
      paintFilters();
      paintTable();

      if (run.status === 'queued' || run.status === 'running') {
        if (pollTimer == null) {
          pollTimer = window.setInterval(load, 4000);
        }
      } else {
        stopPoll();
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      metaHost.replaceChildren(errorBanner(msg, load));
    }
  }

  load();
}
